import SectionCard from './SectionCard'
import { isLoanScenario } from './scenarioType'
import SubsectionDivider from './SubsectionDivider'
import FieldRow from './FieldRow'
import { DerivedValueBox, EditableValueBox } from './ValueBox'
import PercentDollarRow, { PercentDollarToggleControl } from './PercentDollarRow'
import { formatMoney } from './format'
import { computedFallbackUsable } from './computePayload'

// Tooltip copy is verbatim from canvas-text.27.08.md's BUYING COSTS block -- kept headed that way
// in the source text even though the section itself renames to Acquisition Costs; not reconciled.
// Line breaks (U+2028 in the canvas source) are preserved here as literal \n -- see
// LoanCostsSection.jsx's own copy of this comment for what flattening them to spaces broke.
// Inspection, Loan Closing Costs and Title & Escrow follow Cong's text in letter 115; Property Insurance
// is his text of 14.10 (letter 156), percentage from ARV as the backend computes it.
const TOOLTIPS = {
  inspection:
    'Estimated cost of evaluating the property before purchase, including inspections, appraisals, surveys, and other due-diligence services. This amount is entered separately and is not included in closing costs.',
  loanClosingCosts:
    'Estimated financing fees due at closing. Confirm actual fees and loan terms with the lender’s term sheet.\n\n[Origination Fee + Lender Fees + any interest due at closing]',
  agentFee: 'Commission or assignment fee paid to an agent or wholesaler for sourcing or facilitating the purchase.',
  insurance:
    'Estimated insurance premium paid upfront at or before closing to cover the property during the project. Included in acquisition costs and cash to close. Percentage entries are calculated from the ARV.',
  titleFees:
    'Estimated cost of title work, title insurance, escrow services, recording fees, and other settlement charges.\n\n[Nationwide Planning Range\nCash Purchases: 0.70–1.00% of Purchase Price\nFinanced Purchases: 0.90–1.20% of Purchase Price]',
  totalClosingCosts:
    'Total estimated cost of completing the property purchase, excluding the down payment, purchase price, earnest money, or seller credits.',
  earnestMoney:
    'Good-faith deposit submitted with the purchase agreement and typically credited toward the amount due at closing.',
  // Cong, 07.10 (replaces the canvas text): clarifies the automatic tax proration.
  sellerCredits:
    'Closing credits include any negotiated seller concessions and prorated property taxes. Tax prorations are calculated automatically using available tax-year data and the purchase date.',
  // Pill verbatim from Cong, letter 111, Comment Set #3 item 4 (one formula for cash and financed).
  totalCashToClose:
    'Estimated funds required from the buyer at closing, after earnest money and seller credits are applied.\n\n[Purchase Price (or Down Payment) + Total Closing Costs - Earnest Money - Seller Credits]',
}

// Screen-reader text for the computed tax share shown while the box is blank (our copy).
export const SELLER_CREDITS_COMPUTED_DESCRIPTION = "Calculated as the seller's property tax share up to the purchase date"

// scenario/onFieldChange/onToggleChange/computed: same contract as LoanCostsSection -- README.md
// ("the computed prop contract"). `computeStale`, `computedInput`: as SellingCostsSection's.
// The backend already rounds to 0.1 (workbook I24's MROUND); toFixed only pins the one decimal.
function closingCostsLabel(percent) {
  return Number.isFinite(percent) ? `Total Closing Costs (${percent.toFixed(1)}%)` : 'Total Closing Costs'
}

export default function AcquisitionCostsSection({ scenario, computed, computedInput, computeStale = false, onFieldChange, onToggleChange }) {
  const s = scenario ?? {}
  const toggles = s.toggles ?? {}
  const isLoan = isLoanScenario(s.scenarioType)
  // Blank -> the backend's tax share (buyPropertyTax) is the row, shown as a value once fresh (letter 145
  // item 1); a typed number, 0 included, is the whole row. Same rule as Property Tax Proration in Selling Costs.
  const sellerCreditsBlank = s.buy_seller_credits == null || s.buy_seller_credits === ''
  const sellerCreditsPlaceholder =
    sellerCreditsBlank && computedFallbackUsable(computeStale, computedInput, 'buy_seller_credits')
      ? formatMoney(computed?.buyPropertyTax)
      : null

  return (
    <SectionCard title="Acquisition Costs" total={formatMoney(computed?.acquisitionHeaderTotal)} firstRowGap={16}>
      <div className="flex flex-col">
        <FieldRow label="Inspection & Due Diligence" tooltip={TOOLTIPS.inspection}>
          <EditableValueBox money
            value={String(s.inspection_costs ?? '')}
            onChange={(v) => onFieldChange('inspection_costs', v)}
            ariaLabel="Inspection & Due Diligence"
          />
        </FieldRow>

        {/* Placed here, not around Total Closing Costs' group: the drawn total (11,125) only
            reconciles without Inspection & Due Diligence, despite the canvas instruction reading as a
            relocation into that group -- a backend/frame disagreement, flagged, not resolved here. */}
        <SubsectionDivider testId="acquisition-inspection-divider" className="mt-[8px] mb-[8px]" />

        {/* Cash has no loan, so no row (cash frame: Acquisition 421 vs financed 461, one row + gap). */}
        {isLoan ? (
          <FieldRow label="Loan Closing Costs" tooltip={TOOLTIPS.loanClosingCosts}>
            <DerivedValueBox value={formatMoney(computed?.loan_costs_buy)} ariaLabel="Loan Closing Costs" />
          </FieldRow>
        ) : null}

        <div className={isLoan ? 'mt-[8px]' : undefined}>
          <FieldRow
            label="Agent or Wholesaler Fee"
            tooltip={TOOLTIPS.agentFee}
            control={
              <PercentDollarToggleControl
                toggleValue={toggles.buy_commission}
                onToggleChange={(v) => onToggleChange('buy_commission', v)}
                ariaLabel="Agent or Wholesaler Fee %"
              />
            }
          >
            <PercentDollarRow
              percentValue={String(s.agent_commission1 ?? '')}
              onPercentChange={(v) => onFieldChange('agent_commission1', v)}
              percentAriaLabel="Agent or Wholesaler Fee %"
              dollarValue={String(s.agent_commission2 ?? '')}
              onDollarChange={(v) => onFieldChange('agent_commission2', v)}
              dollarAriaLabel="Agent or Wholesaler Fee $"
              dollarComputedValue={formatMoney(computed?.agentFee)}
              toggleValue={toggles.buy_commission}
            />
          </FieldRow>
        </div>

        <div className="mt-[8px]">
          <FieldRow
            label="Property Insurance"
            tooltip={TOOLTIPS.insurance}
            control={
              <PercentDollarToggleControl
                toggleValue={toggles.insurance_policy}
                onToggleChange={(v) => onToggleChange('insurance_policy', v)}
                ariaLabel="Property Insurance %"
              />
            }
          >
            <PercentDollarRow
              percentValue={String(s.insurance_policy1 ?? '')}
              onPercentChange={(v) => onFieldChange('insurance_policy1', v)}
              percentAriaLabel="Property Insurance %"
              dollarValue={String(s.insurance_policy2 ?? '')}
              onDollarChange={(v) => onFieldChange('insurance_policy2', v)}
              dollarAriaLabel="Property Insurance $"
              dollarComputedValue={formatMoney(computed?.insurance)}
              toggleValue={toggles.insurance_policy}
            />
          </FieldRow>
        </div>

        <div className="mt-[8px]">
          <FieldRow
            label="Title & Escrow Fees"
            tooltip={TOOLTIPS.titleFees}
            control={
              <PercentDollarToggleControl
                toggleValue={toggles.buy_title_fee}
                onToggleChange={(v) => onToggleChange('buy_title_fee', v)}
                ariaLabel="Title & Escrow Fees %"
              />
            }
          >
            <PercentDollarRow
              percentValue={String(s.title_fees1 ?? '')}
              onPercentChange={(v) => onFieldChange('title_fees1', v)}
              percentAriaLabel="Title & Escrow Fees %"
              dollarValue={String(s.title_fees2 ?? '')}
              onDollarChange={(v) => onFieldChange('title_fees2', v)}
              dollarAriaLabel="Title & Escrow Fees $"
              dollarComputedValue={formatMoney(computed?.titleFees)}
              toggleValue={toggles.buy_title_fee}
            />
          </FieldRow>
        </div>

        {/* closingCostsPercent is null (bare label) when closing costs are 0 or purchase price is
            blank -- a "(—)" suffix would read as a broken value beside a real one. */}
        <div className="mt-[8px]">
          <FieldRow label={closingCostsLabel(computed?.closingCostsPercent)} weight="bold" tooltip={TOOLTIPS.totalClosingCosts}>
            {/* "(Acquisition Costs)" disambiguates the testid only -- the visible label stays bare.
                Selling Costs carries its own "Total Closing Costs" row too (features/flip/README.md,
                "ambiguous testids across sections"); identical bare testids would collide the moment
                all sections mount on one page. */}
            <DerivedValueBox value={formatMoney(computed?.closing_costs)} bold topDivider ariaLabel="Total Closing Costs (Acquisition Costs)" />
          </FieldRow>
        </div>

        {/* Required vertical gap, spec 3.2: 20px against the 8px standard, not 8+12 stacked. */}
        <div className="mt-[20px]">
          <FieldRow label="Earnest Money" tooltip={TOOLTIPS.earnestMoney}>
            <EditableValueBox money
              value={String(s.earnest_money ?? '')}
              onChange={(v) => onFieldChange('earnest_money', v)}
              ariaLabel="Earnest Money"
            />
          </FieldRow>
        </div>

        {/* buy_seller_credits replaces the computed tax share rather than adding to it (Cong, letter
            89); computed.sellerCreditsInclTaxProration is the row either way. README.md ("Seller
            Credits (incl. Tax Proration)"). */}
        <div className="mt-[8px]">
          <FieldRow label="Seller Credits (incl. Tax Proration)" tooltip={TOOLTIPS.sellerCredits}>
            <EditableValueBox money
              value={String(s.buy_seller_credits ?? '')}
              onChange={(v) => onFieldChange('buy_seller_credits', v)}
              placeholder={sellerCreditsPlaceholder}
              placeholderDark
              placeholderDescription={SELLER_CREDITS_COMPUTED_DESCRIPTION}
              ariaLabel="Seller Credits (incl. Tax Proration)"
            />
          </FieldRow>
        </div>

        <div className="mt-[8px]">
          <FieldRow label="Total Cash to Close" weight="bold" tooltip={TOOLTIPS.totalCashToClose}>
            <DerivedValueBox value={formatMoney(computed?.c2c)} bold topDivider ariaLabel="Total Cash to Close" />
          </FieldRow>
        </div>
      </div>
    </SectionCard>
  )
}
